import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Link } from "gatsby";
import { Col, Row } from "react-bootstrap";
import Moment from "moment";
import { INLINES, BLOCKS } from "@contentful/rich-text-types";

import GatsbyImageWithSVGSupport from "./GatsbyImageWithSVGSupport";
import RichTextBlock from "./RichTextBlock";
import BlogPostTopicsLinks from "./BlogPostTopicsLinks";

import "../styles/components/ArticleCard.scss";

const EXCERPT_RENDERERS = {
    blocks: {
        [BLOCKS.HEADING_1]: (_, children) => <p>{children}</p>,
        [BLOCKS.HEADING_2]: (_, children) => <p>{children}</p>,
        [BLOCKS.HEADING_3]: (_, children) => <p>{children}</p>,
        [BLOCKS.HEADING_4]: (_, children) => <p>{children}</p>,
        [BLOCKS.HEADING_5]: (_, children) => <p>{children}</p>,
        [BLOCKS.HEADING_6]: (_, children) => <p>{children}</p>,
        [INLINES.HYPERLINK]: (node, children) => {
            const { uri } = node.data;
            return (
                <a href={uri} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            );
        },
    },
};

const ArticleCard = ({ post, featured = false, urlPrefix = "/blog" }) => (
    <article className="article-card">
        <Row>
            {post.image && (
                <Col sm={12} md={featured ? 6 : 12}>
                    <Link className="link" to={`${urlPrefix}/${post.slug}/`}>
                        <GatsbyImageWithSVGSupport
                            image={post.image}
                            className="image"
                        />
                    </Link>
                </Col>
            )}
            <Col
                className={classNames("d-flex flex-column info", {
                    "my-auto": featured,
                })}
                sm={12}
                md={featured ? 6 : 12}
            >
                {post.topics && (
                    <div className="topics">
                        <BlogPostTopicsLinks
                            topics={post.topics}
                            urlPrefix={urlPrefix}
                        />
                    </div>
                )}
                <h5 className="title">
                    <Link to={`${urlPrefix}/${post.slug}/`}>{post.title}</Link>
                </h5>
                <time
                    itemProp="datePublished"
                    content={Moment(post.publishDate).format(
                        "YYYY-MM-DDThh:mm:ss.sTZD"
                    )}
                    dateTime={Moment(post.publishDate).format("YYYY-MM-DD")}
                    className="publish-date"
                >
                    {Moment(post.publishDate).format("MMMM D, YYYY")}
                </time>
                <div className="excerpt d-none d-md-block">
                    <RichTextBlock
                        content={post.body}
                        truncate={1}
                        renderers={EXCERPT_RENDERERS}
                    />
                </div>
                <Link className="read-more" to={`${urlPrefix}/${post.slug}/`}>
                    Read More <span className="arrow">&rarr;</span>
                </Link>
            </Col>
        </Row>
    </article>
);

ArticleCard.propTypes = {
    post: PropTypes.shape({
        title: PropTypes.string,
        slug: PropTypes.string,
        publishDate: PropTypes.string,
        image: PropTypes.shape({
            gatsbyImageData: PropTypes.object,
        }),
        topics: PropTypes.arrayOf(
            PropTypes.shape({
                slug: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
            })
        ),
        body: PropTypes.shape({ raw: PropTypes.string.isRequired }),
    }).isRequired,
    featured: PropTypes.bool,
    urlPrefix: PropTypes.string,
};

export default ArticleCard;
